import type {
  HeroContent,
  ImageSlideContent,
  QualitySlideContent,
  MapSlideContent,
  AboutTrailerContent,
  Product,
  ProductsPageContent,
  Service,
  ServicesPageContent,
  TeamMember,
  TeamPageContent,
  Partner,
  HomeStat,
  ContactInfo,
  ContactPageContent,
  CustomGradingContent,
  GrainSpecificationsContent,
  AboutPageContent,
  ServicesCutInContent,
  TrustCoreContent,
  ProductsBentoContent,
  TrackRecordContent,
  PartnersSectionContent,
} from './types';

export const defaultHeroContent: HeroContent = {
  title: 'Reliable Bulk Supply &',
  titleHighlight: 'Distribution',
  subtitle:
    'Supplying Institutions, NGOs & Businesses with quality agro products across East Africa.',
  stats: [
    { value: '5000+', label: 'Tons Supplied', suffix: '' },
    { value: '30+', label: 'Active Suppliers', suffix: '' },
    { value: '50+', label: 'Active Partners', suffix: '' },
    { value: '200+', label: 'Deliveries', suffix: '' },
  ],
  ctas: [
    { text: 'Explore Products', href: '/products', variant: 'primary' },
    { text: 'Request Quote', href: '/contact', variant: 'secondary' },
  ],
  isActive: true,
};

export const defaultImageSlide: ImageSlideContent = {
  brandLabel: 'Revian Agro Supply',
  imageUrl: '/custom_content/nicetruck.jpeg',
  phrases: [
    { line1: 'Consistent Supply,', line2: 'Guaranteed' },
    { line1: 'Across Borders,', line2: 'On Time' },
    { line1: 'Trusted by', line2: 'Institutions & NGOs' },
  ],
  link: '/products',
  order: 1,
};

export const defaultQualitySlide: QualitySlideContent = {
  title: 'Grain You Can',
  tagline: 'Trust Every Time',
  videoUrl: '/custom_content/skstorevid.mp4',
  points: [
    'Visual inspection & grade consistency checks',
    'Moisture & contamination testing on every batch',
    'Secure storage conditions maintained',
  ],
  metrics: [
    { value: '99.5%', label: 'Quality Rate' },
    { value: '3', label: 'Countries' },
    { value: '5+', label: 'Products' },
  ],
  order: 3,
};

export const defaultMapSlide: MapSlideContent = {
  title: 'Regional Reach',
  subtitle: 'Across East Africa',
  description: 'Connecting grain markets across the region',
  countries: [
    { name: 'Uganda' },
    { name: 'Kenya' },
    { name: 'Tanzania' },
  ],
  order: 2,
};

export const defaultHomeStats: HomeStat[] = [
  { value: 5000, display: '5,000+', label: 'Tons Supplied', prefix: '' },
  { value: 30, display: '30+', label: 'Active Suppliers', prefix: '' },
  { value: 50, display: '50+', label: 'Active Partners', prefix: '' },
  { value: 200, display: '200+', label: 'Deliveries', prefix: '' },
];

export const defaultAboutTrailer: AboutTrailerContent = {
  title: 'Structured Supply. Trusted Delivery.',
  description:
    'Founded in 2020, Revian delivers agro products through reliable sourcing, structured systems, and efficient distribution across East Africa. Every transaction strengthens the long term partnerships we build with institutions, NGOs, and businesses.',
  highlights: [
    {
      icon: 'CheckCircle',
      title: 'Consistent Supply',
      description: 'Reliable volumes secured in advance across harvest cycles',
    },
    {
      icon: 'CheckCircle',
      title: 'Transparent Pricing',
      description: 'Market-monitored pricing with no hidden costs',
    },
    {
      icon: 'CheckCircle',
      title: 'Delivery Support',
      description: 'Scheduled dispatch with clear communication throughout',
    },
  ],
  ctaText: 'Discover Our Story',
  ctaLink: '/about',
  mediaType: 'video',
  mediaUrl: '/custom_content/warehousegreensks2.jpeg',
  isActive: true,
};

export const defaultPartners: Partner[] = [
  { name: 'Kisenyi Online', logo: '/partners/kisenyionlinelogo.jpg', category: 'partner', isActive: true },
  { name: 'Grain Bora', logo: '/partners/grainbora.png', category: 'partner', isActive: true },
  { name: 'BGM', logo: '/partners/bgm.png', category: 'partner', isActive: true },
];

export const defaultContactInfo: ContactInfo = {
  email: 'info@reviansupplies.com',
  phone: '+256 700 000 000',
  address: 'Kampala, Uganda',
  socialLinks: [
    { platform: 'x', url: 'https://x.com' },
    { platform: 'tiktok', url: 'https://tiktok.com' },
    { platform: 'instagram', url: 'https://instagram.com' },
  ],
};

export const defaultContactPage: ContactPageContent = {
  headerTitle: "Let's Start a",
  headerHighlight: 'Conversation',
  headerDescription:
    'Whether you need 50 tons or 5,000, we have the capacity and expertise to deliver. Get in touch to discuss your requirements.',
  mediaImages: [
    '/custom_content/sksinstore2.jpeg',
    '/custom_content/warehousegreensks2.jpeg',
    '/custom_content/nicetruck.jpeg',
  ],
  whyChooseTitle: 'Why Choose Revian?',
  whyChoosePoints: [
    'Over 5,000 tons of grain successfully delivered',
    '98% on-time delivery rate',
    '50+ active partnerships across East Africa',
    'Complete supply chain management',
    'Cross-border logistics expertise',
    'Transparent pricing and documentation',
  ],
  formTitle: 'Send Us a Message',
  submitButtonText: 'Send Message',
  successMessage: "Thank you for your message! We'll get back to you within 24 hours.",
};

export const defaultCustomGrading: CustomGradingContent = {
  title: 'Custom',
  titleHighlight: 'Grading',
  subtitle: 'We grade grain to your exact specification, not just what is available.',
  intro:
    "Revian's custom grading service allows processors, exporters, and institutional buyers to specify the exact grade profile they need. We handle sorting, moisture adjustment, and grade documentation to match your procurement contract requirements.",
  images: ['/custom_content/machinesandsks.jpeg', '/custom_content/bluemachine.jpeg'],
  whoTitle: 'Who It Is For',
  whoDescription:
    'Custom grading is available to buyers with specific, documented grade requirements that standard procurement cannot consistently fulfil.',
  useCases: [
    {
      icon: 'Ship',
      title: 'Exporters',
      description:
        'Needing grade certified documentation for customs and cross border trade compliance. We issue formal grade certificates that satisfy phytosanitary and customs requirements.',
    },
    {
      icon: 'Factory',
      title: 'Processors',
      description:
        'Requiring consistent sieve specification per batch. Whether it is a milling target or processing grade threshold, we sort to match your input specification exactly.',
    },
    {
      icon: 'Building2',
      title: 'Institutions',
      description:
        'With contractual grade requirements from funders, regulators, or programme mandates. We provide the documentation trail to demonstrate compliance per batch.',
    },
  ],
  processTitle: 'The Grading Process',
  processDescription:
    'Every custom grading order follows the same four step sequence, giving you a consistent, documented outcome regardless of commodity or volume.',
  steps: [
    {
      number: '01',
      title: 'Intake Inspection',
      description:
        'Grain is received and assessed against your stated grade profile. Moisture, purity, and visual characteristics are recorded at the point of entry.',
      icon: 'ScanLine',
    },
    {
      number: '02',
      title: 'Sorting & Cleaning',
      description:
        'Mechanical sorting is applied to meet your target purity and visual grade. Foreign matter, off grade grain, and contaminants are separated and removed.',
      icon: 'Layers',
    },
    {
      number: '03',
      title: 'Moisture Management',
      description:
        'Where required, moisture levels are adjusted before packaging to bring grain within the specified threshold for your contract or end use.',
      icon: 'Droplets',
    },
    {
      number: '04',
      title: 'Grade Documentation',
      description:
        'A formal grade certificate is issued per batch before dispatch. Documentation includes moisture reading, purity result, grade assigned, and batch reference.',
      icon: 'FileText',
    },
  ],
  tiersTitle: 'Grade Tiers Per Commodity',
  tiersDescription:
    'The following grade tiers are available for custom grading orders. If your requirement falls outside these tiers, contact us to discuss a bespoke specification.',
  tiers: [
    { commodity: 'Rice', grades: ['Premium', 'Standard', 'Processing'] },
    { commodity: 'Maize Flour', grades: ['Grade 1', 'Grade 2'] },
    { commodity: 'Groundnuts', grades: ['Grade A', 'Grade B'] },
    { commodity: 'Beans', grades: ['Grade A', 'Grade B'] },
  ],
  legend: ['Top tier', 'Standard tier', 'Processing tier'],
  docTitle: 'Every Batch Comes with Documentation',
  docBody:
    'Our grade certificates are issued per batch and include moisture reading, purity percentage, grade classification, commodity details, and batch reference. This ensures traceability and compliance at every stage of your supply chain, from intake through to end delivery.',
  ctaTitle: 'Request Custom Grading',
  ctaBody:
    'Tell us your commodity, volume, and target grade specification. Our team will confirm feasibility and provide a grading timeline and cost estimate within 48 hours.',
  ctaPrimaryText: 'Request Custom Grading',
  ctaPrimaryLink: '/contact',
  ctaSecondaryText: 'View Grain Specifications',
  ctaSecondaryLink: '/services/grain-specifications',
};

export const defaultGrainSpecifications: GrainSpecificationsContent = {
  title: 'Grain',
  titleHighlight: 'Specifications',
  description:
    'Our acceptance standards ensure consistent quality across every commodity we procure and distribute. All suppliers must meet these specifications before dispatch.',
  images: ['/custom_content/ricehand.jpeg', '/custom_content/qaseeds.jpeg'],
  specs: [
    {
      commodity: 'Rice',
      moistureMax: '14%',
      purityMin: '98%',
      gradeStandards: ['Grade A Premium', 'Grade B Standard', 'Grade C Processing'],
      rejectionCriteria: [
        'Discoloured grain',
        'Broken grains exceeding 5%',
        'Pest damage or infestation signs',
      ],
      specialNotes: 'Phytosanitary certificate required for all cross border consignments.',
    },
    {
      commodity: 'Beans & Peas',
      moistureMax: '13%',
      purityMin: '96%',
      gradeStandards: ['Grade A Clean, sorted', 'Grade B Standard'],
      rejectionCriteria: [
        'Shrivelled or wrinkled grain',
        'Pest damaged beans',
        'Mixed varieties exceeding 3%',
      ],
      specialNotes: 'Visual uniformity check conducted on intake before acceptance.',
    },
    {
      commodity: 'Maize',
      moistureMax: '13.5%',
      purityMin: '97%',
      gradeStandards: ['Grade 1 Dry, clean', 'Grade 2 Standard', 'Feed Grade'],
      rejectionCriteria: [
        'Any aflatoxin presence',
        'Visible mould or fungal growth',
        'Excessive moisture at intake',
      ],
      specialNotes: 'Aflatoxin test required on all batches without exception.',
    },
    {
      commodity: 'Groundnuts',
      moistureMax: '8%',
      purityMin: '95%',
      gradeStandards: ['Shelled Grade A', 'Shelled Grade B', 'Unshelled (Raw)'],
      rejectionCriteria: [
        'Aflatoxin levels exceeding 10 ppb',
        'Rancid or off smell grain',
        'Visible mould',
      ],
      specialNotes: 'Aflatoxin compliance documentation required with each consignment.',
    },
    {
      commodity: 'Sugar',
      moistureMax: '0.5%',
      purityMin: '99%',
      gradeStandards: ['Refined White', 'Brown Sugar', 'Raw Cane'],
      rejectionCriteria: [
        'Foreign matter present',
        'Colour deviation in refined grade',
        'Moisture above threshold',
      ],
      specialNotes: 'Certificate of analysis per batch is preferred for all supply agreements.',
    },
  ],
};

export const defaultAboutPage: AboutPageContent = {
  headerTitle: "Building East Africa's Agro Supply Chain,",
  headerHighlight: 'One Partnership at a Time',
  storyTitle: 'Our Story',
  storyParagraphs: [
    'Revian was founded in 2020 with a clear purpose: to deliver agro products through reliable sourcing, structured systems, and efficient distribution, creating trust and long term partnerships across East Africa.',
    'What started as a vision has grown into an operational network spanning Uganda, Kenya, and Tanzania, supplying institutions, NGOs, and businesses with consistent quality and transparent dealings.',
  ],
  missionTitle: 'Our Mission',
  missionQuote:
    'To deliver agro products through reliable sourcing, structured systems and efficient distribution, creating trust and long term partnerships.',
  storyMediaType: 'video',
  storyMediaUrl: '/custom_content/truckofsks.mp4',
  foundationTitle: 'Our Foundation',
  foundationStats: [
    { value: '2020', label: 'Year Founded' },
    { value: '5+', label: 'Years of Operations & Growth' },
    { value: '3', label: 'Countries: Uganda, Kenya, Tanzania' },
  ],
  valuesTitle: 'What Drives Us',
  valuesDescription:
    'Purpose driven values that shape every procurement decision, every partnership, and every delivery.',
  coreValues: [
    { icon: 'Shield', title: 'Trust', description: 'Our word is our bond. Every commitment we make is a commitment we keep.' },
    { icon: 'TrendingUp', title: 'Volume', description: 'We operate at scale, securing bulk quantities to meet institutional demand.' },
    { icon: 'CheckCircle', title: 'Consistency', description: 'Steady supply across seasons, built on structured procurement cycles.' },
    { icon: 'Shield', title: 'Integrity', description: 'Transparent dealings with suppliers, clients, and every partner in between.' },
    { icon: 'Clock', title: 'Timely Delivery', description: 'Delivery coordination that prioritises your schedule, not ours.' },
  ],
  strengthsTitle: 'Operational Strengths',
  strengths: [
    { title: 'Structured Procurement', description: 'Organised sourcing cycles that eliminate supply gaps.' },
    { title: 'Quality Control', description: 'Every batch checked before dispatch, no exceptions.' },
    { title: 'Supplier Relationships', description: 'Long term partnerships with verified regional producers.' },
    { title: 'Delivery Coordination', description: 'Planned dispatch with real time communication throughout.' },
  ],
  processTitle: 'Our Supply Process',
  processDescription:
    'How we source ensures stable pricing, volume assurance, and reduced supply disruptions for every client we serve.',
  processSteps: [
    { number: '01', title: 'Work with Verified Producers', description: 'We partner with registered wholesalers and regional grain producers with proven track records.' },
    { number: '02', title: 'Monitor Market Price Movements', description: 'Continuous price tracking across markets allows us to secure competitive bulk volumes at the right time.' },
    { number: '03', title: 'Secure Bulk Volumes in Advance', description: 'We lock in volumes ahead of supply cycles to protect our clients from market disruptions.' },
    { number: '04', title: 'Plan Supply Cycles', description: 'Institutional clients receive structured supply schedules aligned with their operational needs.' },
  ],
  relationshipsTitle: 'Strong Supplier Relationships',
  relationshipsDescription:
    'We maintain long term relationships with the key players across the supply chain:',
  network: [
    { icon: 'Package', label: 'Regional grain suppliers' },
    { icon: 'Users', label: 'Local aggregators' },
    { icon: 'TrendingUp', label: 'Transportation partners' },
  ],
  benefitsTitle: 'This network allows us to:',
  benefits: [
    'Access competitive bulk pricing',
    'Maintain steady supply even during market fluctuations',
    'Scale volumes quickly when required',
  ],
  relationshipsNote:
    'Our network strength translates directly into reliability for our clients.',
  relationshipsImage: '/custom_content/skmn.jpeg',
  qualityTitle: 'Quality Control & Product Assurance',
  qualityDescription:
    'Quality is central to our operations. Every batch is checked before dispatch. The goal is simple. Institutions receive exactly what was ordered.',
  qualityImages: [
    '/custom_content/warehousegreensks2.jpeg',
    '/custom_content/machinesandsks.jpeg',
    '/custom_content/storesks.jpeg',
  ],
  qualityChecks: [
    'Visual inspection for grade consistency',
    'Moisture & contamination checks on every batch',
    'Secure storage conditions maintained throughout',
    'Quality documentation before dispatch',
  ],
  certificationsTitle: 'Certifications & Compliance',
  certificationsDescription:
    'Revian operates in line with regional food safety and trade standards across all three markets.',
  certifications: [
    {
      title: 'UNBS Compliance',
      body: 'Uganda National Bureau of Standards, product and process compliance for food commodities.',
      image: '/custom_content/revianworker.jpeg',
    },
    {
      title: 'Phytosanitary Certification',
      body: 'Export and cross border phytosanitary documentation handled for all consignments.',
      image: '/custom_content/revianshirt.jpeg',
    },
    {
      title: 'Food Safety Standards',
      body: 'Internal quality protocols aligned with regional food safety requirements for bulk supply.',
      image: '/custom_content/skmn.jpeg',
    },
  ],
  promiseTitle: 'Our Promise',
  promiseBody:
    "Whether you're sourcing 50 tons or 5,000, you'll receive the same commitment: quality agro products, transparent dealings, and delivery you can count on.",
};

export const defaultServicesCutIn: ServicesCutInContent = {
  label: 'How We Work',
  title: 'From Procurement to Delivery.',
  videoUrl: '/grain.mp4',
  steps: [
    { number: '01', title: 'Procurement & Sourcing', desc: 'Direct from verified suppliers across East Africa.' },
    { number: '02', title: 'Quality Control', desc: 'Grade checks, moisture analysis, and purity verification.' },
    { number: '03', title: 'Logistics & Documentation', desc: 'Cross border routing, customs clearance, and compliance.' },
    { number: '04', title: 'Delivery & Fulfillment', desc: 'Scheduled dispatch with confirmation at every stage.' },
  ],
  ctaText: 'See How We Work',
  ctaLink: '/services',
};

export const defaultTrustCore: TrustCoreContent = {
  title: 'The Numbers Speak for Themselves',
  description:
    'Every figure here is earned through consistent delivery, verified quality, and the trust of clients who keep coming back. This is what showing up looks like.',
  ctaText: 'View Full Track Record',
  ctaLink: '/track-record',
  stats: [
    { value: '98%', label: 'On Time\nDelivery Rate' },
    { value: '95%', label: 'Client\nRetention Rate' },
    { value: '99.5%', label: 'Quality\nCompliance' },
    { value: '150t', label: 'Average\nTransaction Size' },
  ],
  centerTop: ['Trust', 'Core'],
  centerBottom: ['Revian', 'Standard'],
};

export const defaultProductsBento: ProductsBentoContent = {
  title: 'The Commodities We Move',
  description: 'Five graded agro products, delivered at scale across East Africa.',
  ctaText: 'Browse All',
  ctaLink: '/products',
  products: [
    { name: 'Rice', tag: 'Multiple Grades', image: '/custom_content/riceinhand.jpeg' },
    { name: 'Maize', tag: 'Bulk Supply', image: '/custom_content/greensks.jpeg' },
    { name: 'Sugar', tag: 'Refined & Raw', image: '/custom_content/yellowsks.jpeg' },
    { name: 'Beans & Peas', tag: 'Clean & Sorted', image: '/custom_content/bluesk.jpeg' },
    { name: 'Groundnuts', tag: 'Aflatoxin Checked', image: '/custom_content/qaseeds.jpeg' },
  ],
};

export const defaultTrackRecord: TrackRecordContent = {
  headerTitle: 'The Numbers Speak for',
  headerHighlight: 'Themselves',
  headerDescription:
    'Five years of consistent growth, reliable delivery, and partnerships built on trust, supplying institutions, NGOs, and businesses across East Africa.',
  headerNote:
    'These are not just numbers. They are commitments kept, partnerships strengthened, and promises delivered across Uganda, Kenya, and Tanzania.',
  mainStats: [
    { value: '5,000+', label: 'Tons Supplied', suffix: '' },
    { value: '30+', label: 'Active Suppliers', suffix: '' },
    { value: '50+', label: 'Active Partners', suffix: '' },
    { value: '200+', label: 'Cross Border Deliveries', suffix: '' },
  ],
  additionalMetrics: [
    { metric: 'On Time Delivery Rate', value: '98%' },
    { metric: 'Client Retention Rate', value: '95%' },
    { metric: 'Quality Compliance', value: '99.5%' },
    { metric: 'Average Transaction Size', value: '150 tons' },
  ],
  photoRowImages: [
    '/custom_content/redsksonatruck.jpeg',
    '/custom_content/twotrucksroad.jpeg',
    '/custom_content/manontruck.jpeg',
  ],
  testimonialsTitle: 'What Clients Say',
  testimonialsDescription:
    "Don't just take our word for it. Here's what our clients have to say about working with Revian.",
  testimonials: [
    {
      quote:
        'Revian has been our go to supplier for organic rice for over two years. Their consistency in quality and delivery is unmatched in the region.',
      author: 'Sarah Nakato',
      role: 'Procurement Manager',
      company: 'Premium Foods Ltd',
      image: '',
    },
    {
      quote:
        'Working with Revian transformed our supply chain. They handle everything from sourcing to cross border logistics with complete transparency.',
      author: 'James Ochieng',
      role: 'Operations Director',
      company: 'East Africa Distributors',
      image: '',
    },
    {
      quote:
        'The trust factor is huge in this business. Revian delivers on their promises every single time. That reliability is worth its weight in gold.',
      author: 'Amina Hassan',
      role: 'Managing Partner',
      company: 'Continental Grains',
      image: '',
    },
  ],
  timelineTitle: 'Key Milestones',
  timelineSubtitle: 'Five years building something that lasts.',
  timelineDescription:
    'From the first major supply contract to cross border scale, each year shows compounding trust.',
  milestonesBackgroundImage: '/custom_content/trucksontheroad.jpeg',
  milestones: [
    {
      year: '2020',
      title: 'Founded',
      description:
        'Revian launched with a clear purpose: reliable agro supply and distribution to institutions, NGOs, and businesses across East Africa.',
    },
    {
      year: '2021',
      title: 'Network Built',
      description:
        'Established supplier relationships with regional grain producers and local aggregators, securing consistent bulk sourcing.',
    },
    {
      year: '2022',
      title: 'Regional Expansion',
      description:
        'Expanded cross border operations to Kenya and Tanzania, adding regional delivery routing and documentation capabilities.',
    },
    {
      year: '2023',
      title: '1,000 Tons Milestone',
      description:
        'Reached 1,000 tons in total supply, solidifying long term partnerships with institutional buyers.',
    },
    {
      year: '2024',
      title: '5,000+ Tons & Growing',
      description:
        'Surpassed 5,000 tons supplied with 30+ active suppliers and 50+ active partners across three countries.',
    },
  ],
  ctaTitle: 'Ready to Join Our Network?',
  ctaDescription:
    "Become part of East Africa's most trusted grain trading network. Let's build something great together.",
  ctaPrimaryText: 'Get Started',
  ctaPrimaryLink: '/contact',
  ctaSecondaryText: 'View Products',
  ctaSecondaryLink: '/products',
};

export const defaultPartnersSection: PartnersSectionContent = {
  title: 'Trusted Partnerships',
  description:
    'Building partnerships across East Africa with leading suppliers, distributors, and businesses.',
};

export const defaultServices: Service[] = [
  {
    title: 'Procurement & Sourcing',
    description:
      'Direct relationships with certified organic farms across East Africa. We source the highest quality grains at competitive prices.',
    icon: 'Package',
    features: ['Farm partnerships', 'Quality verification', 'Seasonal planning', 'Volume flexibility'],
    isActive: true,
  },
  {
    title: 'Quality Assurance',
    description:
      'Comprehensive testing and certification processes ensure every batch meets international standards and your specifications.',
    icon: 'Shield',
    features: ['Lab testing', 'Certification management', 'Moisture analysis', 'Purity checks'],
    isActive: true,
  },
  {
    title: 'Logistics & Transport',
    description:
      'End to end logistics management with reliable cross border coordination across Uganda, Kenya, and Tanzania.',
    icon: 'Truck',
    features: ['Cross border expertise', 'Temperature control', 'Real time tracking', 'Insurance coverage'],
    isActive: true,
  },
  {
    title: 'Warehousing & Storage',
    description:
      'Secure storage facilities with optimal conditions to maintain grain quality throughout the supply chain.',
    icon: 'MapPin',
    features: ['Climate control', 'Pest management', 'Inventory tracking', 'Flexible terms'],
    isActive: true,
  },
  {
    title: 'Documentation & Compliance',
    description:
      'Complete handling of export and import documentation, permits, and regulatory compliance across all three countries.',
    icon: 'FileCheck',
    features: ['Export and import permits', 'Customs clearance', 'Phytosanitary certificates', 'Origin documentation'],
    isActive: true,
  },
  {
    title: 'Delivery & Fulfillment',
    description:
      'Timely deliveries with flexible scheduling to match your business needs and production timelines.',
    icon: 'Clock',
    features: ['Scheduled deliveries', 'Just in time supply', 'Order tracking', 'Confirmation reports'],
    isActive: true,
  },
];

export const defaultServicesPage: ServicesPageContent = {
  headerTitle: 'From Farm to Destination',
  headerHighlight: 'We Handle It All',
  headerDescription: 'Complete supply chain management so you can focus on growing your business.',
  services: defaultServices,
  howWeWorkTitle: 'How We Work',
  howWeWorkDescription: 'A streamlined process designed for efficiency, transparency, and reliability.',
  processSteps: [
    {
      number: '01',
      title: 'Consultation',
      description: 'We discuss your needs, volume requirements, and delivery timeline.',
    },
    {
      number: '02',
      title: 'Quotation',
      description: 'Receive a detailed quote with pricing, specifications, and terms.',
    },
    {
      number: '03',
      title: 'Execution',
      description: 'We handle procurement, quality checks, documentation, and logistics.',
    },
    {
      number: '04',
      title: 'Delivery',
      description: 'Your order arrives on time with full documentation and quality reports.',
    },
  ],
  deliveryTitle: 'Delivery Coordination & Logistics',
  deliveryDescription:
    "We don't just supply. We coordinate. Our delivery process is built around scheduled planning, clear communication, and regional routing expertise.",
  howWeDeliverPoints: [
    'Scheduled dispatch planning aligned with your intake schedule',
    'Clear communication at every stage of the delivery process',
    'Regional delivery routing across Uganda, Kenya & Tanzania',
  ],
  contractedClientsPoints: [
    'Recurring supply schedules tailored to your consumption cycle',
    'Emergency replenishment support when needed',
  ],
  deliveryImage: '/custom_content/nicetruck.jpeg',
  ctaTitle: 'Ready to Discuss Your Supply Needs?',
  ctaDescription: 'Contact our team for any of the following:',
  ctaPoints: [
    'Bulk quotation',
    'Supply capacity briefing',
    'Scheduled delivery plan',
    'Downloadable company profile',
  ],
  ctaVideo: '/custom_content/processingmachines.mp4',
};

export const defaultTeamMembers: TeamMember[] = [
  {
    name: 'David Mwangi',
    role: 'Founder & CEO',
    bio: '',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
    isActive: true,
  },
  {
    name: 'Grace Nakato',
    role: 'Operations Director',
    bio: '',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400',
    isActive: true,
  },
  {
    name: 'Samuel Ochieng',
    role: 'Head of Procurement',
    bio: '',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400',
    isActive: true,
  },
  {
    name: 'Fatima Hassan',
    role: 'Quality Assurance Manager',
    bio: '',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
    isActive: true,
  },
  {
    name: 'Peter Kamau',
    role: 'Logistics Coordinator',
    bio: '',
    image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400',
    isActive: true,
  },
  {
    name: 'Amina Auma',
    role: 'Business Development Manager',
    bio: '',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400',
    isActive: true,
  },
];

export const defaultTeamPage: TeamPageContent = {
  headerTitle: 'Meet the Team Behind',
  headerHighlight: "Revian's Success",
  headerDescription:
    'A dedicated team of professionals committed to excellence, trust, and reliable service across East Africa.',
  ctaTitle: 'Join Our Growing Team',
  ctaDescription:
    "We're always looking for talented professionals who share our commitment to excellence and integrity. If you're passionate about agricultural trading and supply chain management, we'd love to hear from you.",
  ctaButtonText: 'Explore Career Opportunities',
  ctaEmail: 'careers@reviansupplies.com',
};

export const defaultProductsPage: ProductsPageContent = {
  headerTitle: 'Agro Products at',
  headerHighlight: 'Scale',
  headerDescription: 'Rice, beans, maize, groundnuts, and sugar, supplied consistently to institutions, NGOs, and businesses across East Africa.',
  capacityTitle: 'Show Capacity Confidently',
  capacityDescription: 'Whether you need a one off bulk purchase or a recurring supply contract, Revian has the sourcing network and logistics capacity to deliver. We handle volumes from 2 tons to 1,000+ tons across all product lines.',
  capacityStats: [
    { value: '5,000+', label: 'Tons Supplied' },
    { value: '30+', label: 'Active Suppliers' },
    { value: '5', label: 'Product Lines' },
    { value: '3', label: 'Countries' },
  ],
  ctaTitle: 'Ready to Discuss Your Supply Needs?',
  ctaDescription: 'Contact our team for a bulk quotation, supply capacity briefing, or to request our company profile.',
  ctaButton1Text: 'Request a Bulk Quote',
  ctaButton1Link: '/contact',
  ctaButton2Text: 'See Our Services',
  ctaButton2Link: '/services',
};

export const defaultProducts: Product[] = [
  {
    id: 'rice',
    name: 'Rice',
    tag: 'Multiple Grades',
    description:
      'Available in multiple grades for institutional, retail, and processing requirements. Sourced from verified producers and checked for purity and moisture before dispatch.',
    image: '/custom_content/riceinhand.jpeg',
    grades: ['Grade A (Premium)', 'Grade B (Standard)', 'Grade C (Processing)'],
    bulkCapacity: 'Minimum 5 tons | Up to 500+ tons per order',
    packaging: ['25kg bags', '50kg bags', 'Bulk loose (negotiable)'],
    delivery: ['Scheduled delivery to institution', 'Recurring supply available', 'Cross border (KE, TZ)'],
    isActive: true,
  },
  {
    id: 'beans',
    name: 'Beans & Peas',
    tag: 'Clean & Sorted',
    description:
      'A staple for institutions and food processors. We supply clean, sorted beans and peas with consistent sizing and low defect rates.',
    image: '/custom_content/bluesk.jpeg',
    grades: ['Grade A (Clean, sorted)', 'Grade B (Standard)'],
    bulkCapacity: 'Minimum 3 tons | Up to 200+ tons per order',
    packaging: ['25kg bags', '50kg bags', 'Custom packaging on request'],
    delivery: ['Scheduled dispatch', 'Emergency replenishment for contracted clients', 'Regional delivery routing'],
    isActive: true,
  },
  {
    id: 'maize',
    name: 'Maize',
    tag: 'Bulk Supply',
    description:
      'Bulk maize supply for processors, animal feed manufacturers, and institutional kitchens. Grade consistent with low moisture content.',
    image: '/custom_content/greensks.jpeg',
    grades: ['Grade 1 (Dry, clean)', 'Grade 2 (Standard)', 'Feed Grade'],
    bulkCapacity: 'Minimum 10 tons | Up to 1,000+ tons per order',
    packaging: ['50kg bags', '90kg bags', 'Loose bulk (truckload)'],
    delivery: ['Scheduled dispatch', 'Recurring supply for contracted buyers', 'Cross border logistics available'],
    isActive: true,
  },
  {
    id: 'groundnuts',
    name: 'Groundnuts',
    tag: 'Aflatoxin Checked',
    description:
      'Shelled and unshelled groundnuts for food processors, NGO feeding programmes, and retail distribution. Checked for aflatoxin compliance.',
    image: '/custom_content/qaseeds.jpeg',
    grades: ['Shelled Grade A', 'Shelled Grade B', 'Unshelled (raw)'],
    bulkCapacity: 'Minimum 2 tons | Up to 100+ tons per order',
    packaging: ['25kg bags', '50kg bags', 'Custom sacks on request'],
    delivery: ['Scheduled delivery', 'Nationwide Uganda delivery', 'Cross border (KE, TZ)'],
    isActive: true,
  },
  {
    id: 'sugar',
    name: 'Sugar',
    tag: 'Refined & Raw',
    description:
      'Refined and raw sugar for institutional kitchens, food processors, and distributors. Consistent quality with competitive bulk pricing.',
    image: '/custom_content/yellowsks.jpeg',
    grades: ['Refined White', 'Brown Sugar', 'Raw Cane Sugar'],
    bulkCapacity: 'Minimum 5 tons | Up to 300+ tons per order',
    packaging: ['2kg packs', '25kg bags', '50kg bags'],
    delivery: ['Scheduled dispatch', 'Institutional supply schedules', 'Regional distribution'],
    isActive: true,
  },
];
